import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/AuthContext';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const formaterErreur = (d) => (d?.erreurs?.length ? d.erreurs.join(' · ') : d?.message || (typeof d === 'string' ? d : 'Erreur lors de la sauvegarde.'));

function Champ({ cle, spec, valeur, onChange, onDefaut }) {
  const estBool = spec.type === 'bool';
  const estTexte = spec.type === 'texte';
  return (
    <div className="flex flex-wrap items-center gap-3 py-2 border-b last:border-0" data-testid={`param-${cle}`}>
      <div className="flex-1 min-w-[220px]">
        <p className="text-sm font-medium">{spec.libelle}</p>
        <p className="text-xs text-slate-500">Défaut : {estTexte ? 'gabarit standard' : estBool ? (spec.defaut ? 'oui' : 'non') : spec.defaut}{!estBool && !estTexte && ` · entre ${spec.min} et ${spec.max}`}</p>
      </div>
      {estTexte ? (
        <textarea className="w-full min-h-[120px] text-sm border rounded p-2 font-mono" value={valeur ?? ''} maxLength={spec.max_longueur} onChange={(e) => onChange(e.target.value)} data-testid={`param-${cle}-texte`} />
      ) : estBool ? (
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!valeur} onChange={(e) => onChange(e.target.checked)} data-testid={`param-${cle}-valeur`} />{valeur ? 'Oui' : 'Non'}</label>
      ) : (
        <Input type="number" className="w-28" value={valeur ?? ''} min={spec.min} max={spec.max} onChange={(e) => onChange(e.target.value === '' ? '' : parseInt(e.target.value, 10))} data-testid={`param-${cle}-valeur`} />
      )}
      <Button size="sm" variant="ghost" onClick={onDefaut} disabled={valeur === spec.defaut} data-testid={`param-${cle}-defaut`}>Rétablir le défaut</Button>
    </div>
  );
}

function Section({ titre, description, cles: clesDemandees, data, brouillon, setBrouillon, sauvegarder, reinitialiser, testid, children }) {
  const cles = clesDemandees.filter((c) => data.registre[c]);  // clés absentes du registre (ancien serveur) ignorées
  const modifie = cles.some((c) => brouillon[c] !== data.valeurs[c]);
  return (
    <Card data-testid={testid}>
      <CardHeader><CardTitle>{titre}</CardTitle><CardDescription>{description}</CardDescription></CardHeader>
      <CardContent className="space-y-3">
        {cles.map((c) => <Champ key={c} cle={c} spec={data.registre[c]} valeur={brouillon[c]} onChange={(v) => setBrouillon((b) => ({ ...b, [c]: v }))} onDefaut={() => reinitialiser(c)} />)}
        <Button size="sm" disabled={!modifie} onClick={() => sauvegarder(cles)} data-testid={`${testid}-sauvegarder`}>Enregistrer</Button>
        {children}
      </CardContent>
    </Card>
  );
}

// 🔐 Connexion P2 : paramètres de sécurité (propriétaire) — Mot de passe · Verrouillage (+ comptes verrouillés) · Session · Actions.
export function ParametresSecurite() {
  const { updateToken } = useAuth() || {};
  const [data, setData] = useState(null);
  const [brouillon, setBrouillon] = useState({});
  const [verrouilles, setVerrouilles] = useState([]);
  const [sessionsActives, setSessionsActives] = useState(null); // 🔐 P4
  const charger = async () => {
    const r = await axios.get(`${API_URL}/api/security/parametres`);
    setData(r.data); setBrouillon(r.data.valeurs);
  };
  const chargerVerrouilles = () => axios.get(`${API_URL}/api/security/comptes-verrouilles`).then((r) => setVerrouilles(r.data.comptes || [])).catch(() => {});
  useEffect(() => { charger().catch(() => toast.error('Impossible de charger les paramètres de sécurité.')); chargerVerrouilles(); axios.get(`${API_URL}/api/auth/sessions/stats`).then((r) => setSessionsActives(r.data.sessions_actives)).catch(() => {}); }, []);
  if (!data) return null;
  const sauvegarder = async (cles) => {
    const modifs = Object.fromEntries(cles.filter((c) => brouillon[c] !== data.valeurs[c]).map((c) => [c, brouillon[c]]));
    try {
      const r = await axios.put(`${API_URL}/api/security/parametres`, modifs);
      setData(r.data); setBrouillon(r.data.valeurs); toast.success('Paramètres de sécurité enregistrés.');
    } catch (e) { toast.error(formaterErreur(e?.response?.data?.detail)); }
  };
  const reinitialiser = async (cle) => {
    try {
      const r = await axios.post(`${API_URL}/api/security/parametres/${cle}/reinitialiser`);
      setData(r.data); setBrouillon(r.data.valeurs); toast.success('Valeur par défaut rétablie.');
    } catch (e) { toast.error(formaterErreur(e?.response?.data?.detail)); }
  };
  const deverrouiller = async (c) => {
    try {
      await axios.post(`${API_URL}/api/auth/users/${c.user_id}/unlock-account`);
      toast.success(`Compte ${c.email} déverrouillé.`); chargerVerrouilles();
    } catch (e) { toast.error(formaterErreur(e?.response?.data?.detail)); }
  };
  const deconnecterTous = async () => {
    if (!window.confirm('Tous les utilisateurs, vous inclus, devront se reconnecter. Continuer ?')) return;
    try {
      const r = await axios.post(`${API_URL}/api/auth/force-logout-all`);
      if (r.data?.new_token_for_caller && updateToken) updateToken(r.data.new_token_for_caller);
      toast.success('Tous les utilisateurs ont été déconnectés.');
    } catch (e) { toast.error(formaterErreur(e?.response?.data?.detail)); }
  };
  const props = { data, brouillon, setBrouillon, sauvegarder, reinitialiser };
  return (
    <div className="space-y-6" data-testid="parametres-securite">
      <Section testid="section-mot-de-passe" titre="Mot de passe" description="Règles appliquées au changement et à la réinitialisation du mot de passe (affichées en direct aux utilisateurs)."
        cles={['mdp_longueur_min', 'mdp_majuscule', 'mdp_chiffre', 'mdp_minuscule', 'mdp_symbole', 'reset_lien_validite_min', 'invitation_validite_jours']} {...props} />
      <Section testid="section-verrouillage" titre="Verrouillage" description="Après X tentatives échouées, le compte est verrouillé pour la durée indiquée ; les propriétaires peuvent être avisés par courriel."
        cles={['verrou_seuil', 'verrou_duree_min', 'verrou_courriel_proprietaire']} {...props}>
        <div className="pt-3 border-t" data-testid="comptes-verrouilles">
          <p className="text-sm font-semibold mb-2">Comptes verrouillés {verrouilles.length ? `(${verrouilles.length})` : ''}</p>
          {!verrouilles.length && <p className="text-xs text-slate-500" data-testid="comptes-verrouilles-vide">Aucun compte verrouillé.</p>}
          {verrouilles.map((c) => (
            <div key={c.user_id} className="flex flex-wrap items-center justify-between gap-2 p-2 bg-red-50 border border-red-200 rounded mb-1" data-testid={`compte-verrouille-${c.user_id}`}>
              <span className="text-sm">{c.nom} ({c.email}) — {c.minutes_restantes != null ? `${c.minutes_restantes} min restantes` : 'durée inconnue'}{c.ip_dernier_echec ? ` · IP ${c.ip_dernier_echec}` : ''}</span>
              <Button size="sm" variant="outline" onClick={() => deverrouiller(c)} data-testid={`deverrouiller-${c.user_id}`}>Déverrouiller</Button>
            </div>
          ))}
        </div>
      </Section>
      <Section testid="section-session" titre="Session" description="La session expire seulement après X jours sans utilisation lorsque le renouvellement est actif."
        cles={['session_duree_jours', 'session_renouvellement']} {...props}>
        <p className="text-sm text-slate-600" data-testid="sessions-actives-global">Sessions actives (tous les appareils, tous les utilisateurs) : <strong data-testid="sessions-actives-compteur">{sessionsActives ?? '…'}</strong></p>
      </Section>
      <Section testid="section-historique-parametres" titre="Historique de sécurité — rétention" description="Les connexions (réussies/échouées) plus anciennes que la rétention sont purgées chaque nuit. Les événements critiques (verrouillages, réinitialisations, paramètres, accès) et l'historique RH ne sont jamais purgés."
        cles={['historique_retention_jours', 'historique_export_max']} {...props} />
      <Section testid="section-relances" titre="Relances de soumissions" description="Après X jours sans ouverture par le client, une relance est PROPOSÉE dans « Relances à approuver » — jamais envoyée sans approbation. Variables du gabarit : {prenom} {numero} {montant} {lien} {entreprise}."
        cles={['relance_soumission_delai_jours', 'relance_soumission_max', 'relance_soumission_heure', 'relance_soumission_gabarit']} {...props} />
      <Section testid="section-alerte-liens" titre="Alerte liens refusés" description="Résumé quotidien aux propriétaires, envoyé seulement si au moins un lien public a été refusé ou a échoué la veille."
        cles={['alerte_liens_active', 'alerte_liens_heure']} {...props} />
      <Card data-testid="section-actions">
        <CardHeader><CardTitle>Actions</CardTitle><CardDescription>Déconnexion globale : tous les jetons émis avant maintenant sont refusés.</CardDescription></CardHeader>
        <CardContent><Button variant="destructive" onClick={deconnecterTous} data-testid="deconnecter-tout-le-monde">Déconnecter tout le monde</Button></CardContent>
      </Card>
    </div>
  );
}
